import { useEffect, useMemo, useState } from "react";
import AdminUserPicker from './AdminUserPicker';
import { getAdminSettingsPreview, removeAdminCompanyRoute, saveAdminCompanyRoute, saveAdminFallbackHc, saveAdminNotificationRecipients, saveAdminPolicy } from "./workflow-api";
import { mergeSavedAdminSection } from './admin-drafts';

function validateDraft(draft) {
  const reminderCount = Number(draft.approvalReminderCount);
  const reminderIntervalDays = Number(draft.approvalReminderIntervalDays);
  const expiryDays = Number(draft.approvalExpiryDays);
  const retention = Number(draft.retentionDays);
  const errors = [];
  if (!Number.isInteger(reminderCount) || reminderCount < 1 || reminderCount > 5) errors.push("Reminder count must be a whole number from 1 to 5.");
  if (!Number.isFinite(reminderIntervalDays) || reminderIntervalDays <= 0) errors.push("Reminder interval must be greater than zero days.");
  if (!Number.isFinite(expiryDays) || expiryDays <= 0) errors.push("Approval expiry must be greater than zero days.");
  if (Number.isInteger(reminderCount) && reminderCount >= 1 && reminderCount <= 5
      && Number.isFinite(reminderIntervalDays) && reminderIntervalDays > 0
      && Number.isFinite(expiryDays) && expiryDays > 0
      && reminderCount * reminderIntervalDays >= expiryDays) {
    errors.push("Expiry must be later than the final reminder.");
  }
  if (!Number.isInteger(retention) || retention < 1) errors.push("Photo retention must be a whole number of at least 1 day.");
  return errors;
}

function reminderSchedulePreview(draft) {
  const count = Number(draft?.approvalReminderCount);
  const intervalDays = Number(draft?.approvalReminderIntervalDays);
  const expiryDays = Number(draft?.approvalExpiryDays);
  if (!Number.isInteger(count) || count < 1 || count > 5 || !Number.isFinite(intervalDays) || intervalDays <= 0 || !Number.isFinite(expiryDays) || expiryDays <= 0) return "";
  const reminders = Array.from({ length: count }, (_, index) => `day ${Math.round((index + 1) * intervalDays * 10) / 10}`);
  return `Reminders: ${reminders.join(", ")} · Expiry: day ${Math.round(expiryDays * 10) / 10}`;
}

function routeLabel(state) {
  if (state === "ready") return "Ready";
  if (state === "disabled") return "Disabled";
  if (state === "missing_approvers") return "Missing approvers";
  if (state === "invalid_approvers") return "Invalid approvers";
  return state || "Unknown";
}

function writeStatusLabel(status) {
  if (status === "ready") return "Admin write ready";
  if (status === "locked_missing_admin_role") return "Read only";
  if (status === "locked_writer_not_configured") return "Writer not configured";
  return "Write locked";
}

function emailsFromText(value) {
  return [...new Set(String(value || "").split(/[\n,;]+/u).map((item) => item.trim().toLowerCase()).filter(Boolean))];
}

function newRouteDraft() {
  return {
    companyKey: "",
    enabled: true,
    ccdApprovalRequired: true,
    approverEmailsText: "",
    expectedVersion: null,
    removeMode: false,
  };
}

function editableRouteDraft(route) {
  return {
    companyKey: route.companyKey || "",
    enabled: route.enabled !== false,
    ccdApprovalRequired: route.ccdApprovalRequired !== false,
    approverEmailsText: (route.approvers || []).map((approver) => approver.email).filter(Boolean).join("\n"),
    expectedVersion: route.version ?? null,
    removeMode: false,
  };
}

function removalRouteDraft(route) {
  return { ...editableRouteDraft(route), removeMode: true };
}

function fallbackText(settings) {
  return (settings?.fallbackHc?.approvers || []).map((approver) => approver.email).filter(Boolean).join("\n");
}

function supportNotificationText(settings) {
  return (settings?.notifications?.supportEmails || []).join("\n");
}

export default function AdminSettingsPanel({ settings, getAccessToken, onDirtyChange, onBusyChange, activeSection = 'approvals', ccdPanel, accessPanel, externallyBusy = false }) {
  const [currentSettings, setCurrentSettings] = useState(settings || null);
  const [draft, setDraft] = useState(settings?.policy ? { ...settings.policy } : null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [routeDraft, setRouteDraft] = useState(null);
  const [routeReason, setRouteReason] = useState("");
  const [routeSaving, setRouteSaving] = useState(false);
  const [routeError, setRouteError] = useState("");
  const [routeMessage, setRouteMessage] = useState("");
  const [companySearch, setCompanySearch] = useState('');
  const [fallbackEmailsText, setFallbackEmailsText] = useState(fallbackText(settings));
  const [fallbackReason, setFallbackReason] = useState("");
  const [fallbackSaving, setFallbackSaving] = useState(false);
  const [fallbackError, setFallbackError] = useState("");
  const [fallbackMessage, setFallbackMessage] = useState("");
  const [supportEmailsText, setSupportEmailsText] = useState(supportNotificationText(settings));
  const [notificationReason, setNotificationReason] = useState("");
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationError, setNotificationError] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");

  useEffect(() => {
    setCurrentSettings(settings || null);
    if (settings?.policy) setDraft({ ...settings.policy });
    setReason("");
    setSaveError("");
    setSaveMessage("");
    setRouteDraft(null);
    setRouteReason("");
    setRouteError("");
    setRouteMessage("");
    setFallbackEmailsText(fallbackText(settings));
    setFallbackReason("");
    setFallbackError("");
    setFallbackMessage("");
    setSupportEmailsText(supportNotificationText(settings));
    setNotificationReason("");
    setNotificationError("");
    setNotificationMessage("");
  }, [settings?.generatedAt]);

  const errors = useMemo(() => draft ? validateDraft(draft) : [], [draft]);
  const schedulePreview = useMemo(() => reminderSchedulePreview(draft), [draft]);
  const busy = saving || routeSaving || fallbackSaving || notificationSaving;
  const dirty = Boolean(reason || routeDraft || fallbackReason || notificationReason
    || (draft && Object.keys(draft).some(key => String(draft[key]) !== String(currentSettings?.policy?.[key])))
    || fallbackEmailsText !== fallbackText(currentSettings)
    || supportEmailsText !== supportNotificationText(currentSettings));
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);
  useEffect(() => {
    if (!dirty && !busy) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, busy]);
  if (!currentSettings) return null;

  const updatePolicy = (field, value) => setDraft((current) => ({
    ...current,
    [field]: value,
    ...(['approvalReminderCount', 'approvalReminderIntervalDays'].includes(field) ? { approvalReminderScheduleDays: undefined } : {}),
  }));
  const routes = currentSettings.companyRoutes || [];
  const permissions = settings.permissions || {};
  const editableRoutes = currentSettings.editableCompanyRoutes || null;
  const writerReady = settings.writeStatus === "ready" && typeof getAccessToken === "function";
  const visibleRoutes = (editableRoutes || routes).filter(route => [route.companyKey, ...(route.approvers || []).map(person => `${person.name} ${person.email}`)].join(' ').toLowerCase().includes(companySearch.trim().toLowerCase()));
  const reasonReady = reason.trim().length >= 5 && reason.trim().length <= 500;
  const routeReasonReady = routeReason.trim().length >= 5 && routeReason.trim().length <= 500;
  const routeEmails = routeDraft ? emailsFromText(routeDraft.approverEmailsText) : [];
  const routeEditReady = routeDraft
    && !routeDraft.removeMode
    && routeDraft.companyKey.trim().length >= 2
    && (!routeDraft.enabled || routeEmails.length > 0)
    && routeEmails.length <= 10;
  const fallbackEditable = Array.isArray(currentSettings.fallbackHc?.approvers);
  const fallbackEmails = emailsFromText(fallbackEmailsText);
  const fallbackReasonReady = fallbackReason.trim().length >= 5 && fallbackReason.trim().length <= 500;
  const fallbackDraftReady = fallbackEmails.length <= 10;
  const notificationEditable = Array.isArray(currentSettings.notifications?.supportRecipients);
  const supportEmails = emailsFromText(supportEmailsText);
  const notificationReasonReady = notificationReason.trim().length >= 5 && notificationReason.trim().length <= 500;
  const notificationDraftReady = supportEmails.length >= 1 && supportEmails.length <= 10;

  function refreshLocalSettings(result, section) {
    setCurrentSettings(current => mergeSavedAdminSection(current, result, section));
    if (section === 'policy') setDraft({ ...result.policy });
    if (section === 'fallback') setFallbackEmailsText(fallbackText(result));
    if (section === 'notifications') setSupportEmailsText(supportNotificationText(result));
  }

  function beginAddRoute() {
    if (routeDraft && !window.confirm('Discard the current company draft?')) return;
    setRouteDraft(newRouteDraft());
    setRouteReason("");
    setRouteError("");
    setRouteMessage("");
  }

  function beginEditRoute(route) {
    if (routeDraft && !window.confirm('Discard the current company draft?')) return;
    if (route.approvers?.some(person => !person.resolved || !person.email)) {
      setRouteError('Some HC approvers could not be loaded. Refresh before editing so existing approvers are not accidentally removed.');
      return;
    }
    setRouteDraft(editableRouteDraft(route));
    setRouteReason("");
    setRouteError("");
    setRouteMessage("");
  }

  function beginRemoveRoute(route) {
    if (routeDraft && !window.confirm('Discard the current company draft?')) return;
    setRouteDraft(removalRouteDraft(route));
    setRouteReason("");
    setRouteError("");
    setRouteMessage("");
  }

  function cancelRouteEdit() {
    if (routeSaving) return;
    setRouteDraft(null);
    setRouteReason("");
    setRouteError("");
  }

  async function savePolicy() {
    if (!permissions.managePolicy || !writerReady || errors.length || !reasonReady || saving) return;
    setSaving(true);
    setSaveError("");
    setSaveMessage("");
    try {
      const result = await saveAdminPolicy(draft, currentSettings.policyVersion, reason, getAccessToken);
      refreshLocalSettings(result, 'policy');
      setReason("");
      setSaveMessage("Policy saved and audited.");
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
    } finally {
      setSaving(false);
    }
  }

  async function saveRoute() {
    if (!permissions.manageHcRouting || !writerReady || !routeEditReady || !routeReasonReady || routeSaving) return;
    setRouteSaving(true);
    setRouteError("");
    setRouteMessage("");
    try {
      const result = await saveAdminCompanyRoute({
        companyKey: routeDraft.companyKey,
        enabled: routeDraft.enabled,
        ccdApprovalRequired: routeDraft.ccdApprovalRequired,
        approverEmails: routeEmails,
      }, routeDraft.expectedVersion, routeReason, getAccessToken);
      refreshLocalSettings(result, 'routes');
      setRouteDraft(null);
      setRouteReason("");
      setRouteMessage("Company approval routing saved and audited.");
    } catch (error) {
      setRouteError(error instanceof Error ? error.message : String(error));
    } finally {
      setRouteSaving(false);
    }
  }

  async function removeRoute() {
    if (!permissions.manageHcRouting || !writerReady || !routeDraft?.removeMode || !routeReasonReady || routeSaving) return;
    const confirmed = window.confirm(`Remove ${routeDraft.companyKey} from company approval routing?`);
    if (!confirmed) return;
    setRouteSaving(true);
    setRouteError("");
    setRouteMessage("");
    try {
      const companyKey = routeDraft.companyKey;
      await removeAdminCompanyRoute(companyKey, routeDraft.expectedVersion, routeReason, getAccessToken);
      // The delete has succeeded even if refreshing the list fails afterwards.
      setCurrentSettings(current => ({ ...current,
        editableCompanyRoutes: current.editableCompanyRoutes?.filter(route => route.companyKey !== companyKey),
        companyRoutes: current.companyRoutes?.filter(route => route.companyKey !== companyKey),
      }));
      setRouteDraft(null);
      setRouteReason('');
      setRouteMessage(`Company removed: ${companyKey}`);
      const refreshed = await getAdminSettingsPreview(getAccessToken);
      refreshLocalSettings(refreshed, 'routes');
    } catch (error) {
      setRouteError(error instanceof Error ? error.message : String(error));
    } finally {
      setRouteSaving(false);
    }
  }

  async function saveFallback() {
    if (!permissions.manageHcRouting || !writerReady || !fallbackEditable || !fallbackDraftReady || !fallbackReasonReady || fallbackSaving) return;
    setFallbackSaving(true);
    setFallbackError("");
    setFallbackMessage("");
    try {
      const result = await saveAdminFallbackHc(fallbackEmails, currentSettings.fallbackHc?.version ?? null, fallbackReason, getAccessToken);
      refreshLocalSettings(result, 'fallback');
      setFallbackReason("");
      setFallbackMessage(fallbackEmails.length ? "Central fallback HC saved and audited." : "Central fallback HC disabled and audited.");
    } catch (error) {
      setFallbackError(error instanceof Error ? error.message : String(error));
    } finally {
      setFallbackSaving(false);
    }
  }

  async function saveNotifications() {
    if (!permissions.manageNotifications || !writerReady || !notificationEditable || !notificationDraftReady || !notificationReasonReady || notificationSaving) return;
    setNotificationSaving(true);
    setNotificationError("");
    setNotificationMessage("");
    try {
      const result = await saveAdminNotificationRecipients({ supportEmails }, currentSettings.notifications?.version ?? null, notificationReason, getAccessToken);
      refreshLocalSettings(result, 'notifications');
      setNotificationReason("");
      setNotificationMessage("Support recipients saved and audited.");
    } catch (error) {
      setNotificationError(error instanceof Error ? error.message : String(error));
    } finally {
      setNotificationSaving(false);
    }
  }

  return <section className="admin-settings-panel">
    {currentSettings.compatibilityNotice && <p className="workflow-error" role="status">{currentSettings.compatibilityNotice}</p>}
    <div className="admin-edit-status"><span className={writerReady ? 'admin-lock ready' : 'admin-lock'}>{writeStatusLabel(settings.writeStatus)}</span></div>
    {permissions.manageHcRouting && <label className="admin-company-search" hidden={activeSection !== 'approvals'}>Find a company or HC approver<input type="search" value={companySearch} onChange={event => setCompanySearch(event.target.value)} placeholder="Search companies and approvers" /></label>}

    <fieldset className="admin-editable-sections" disabled={!writerReady || busy || externallyBusy}>
    {permissions.managePolicy && <section className="admin-section" hidden={activeSection !== 'policy'}>
      <div className="admin-section-heading"><span>01</span><div><h4>Workflow settings</h4><small>Timing and retention policy only.</small></div></div>
      <div className="admin-settings-grid single-column">
        <section className="admin-settings-card">
          <h5>Approval SLA & retention</h5>
          <small>Source: {currentSettings.policySource === "admin-settings" ? "Admin settings store" : "Environment defaults"}</small>
          <div className="admin-field-grid">
            <label>Number of reminders<select value={draft?.approvalReminderCount ?? 2} onChange={(event) => updatePolicy("approvalReminderCount", event.target.value)}>{[1, 2, 3, 4, 5].map((count) => <option value={count} key={count}>{count}</option>)}</select></label>
            <label>Reminder interval (days)<input type="number" min="0.1" step="0.1" value={draft?.approvalReminderIntervalDays ?? ""} onChange={(event) => updatePolicy("approvalReminderIntervalDays", event.target.value)} /></label>
            <label>Expire after (days)<input type="number" min="0.1" step="0.1" value={draft?.approvalExpiryDays ?? ""} onChange={(event) => updatePolicy("approvalExpiryDays", event.target.value)} /></label>
            <label>Photo retention (days)<input type="number" min="1" step="1" value={draft?.retentionDays ?? ""} onChange={(event) => updatePolicy("retentionDays", event.target.value)} /></label>
          </div>
          {schedulePreview && <small>{schedulePreview}</small>}
          {errors.map((error) => <small className="admin-validation-error" key={error}>{error}</small>)}
          <label className="admin-change-reason">Change reason<textarea rows="2" maxLength="500" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why is this policy being changed?" /></label>
          {reason.length > 0 && !reasonReady && <small className="admin-validation-error">Enter 5–500 characters for the audit reason.</small>}
          {saveError && <small className="admin-validation-error">{saveError}</small>}
          {saveMessage && <small className="admin-save-success">{saveMessage}</small>}
          <button onClick={savePolicy} disabled={!writerReady || errors.length > 0 || !reasonReady || saving} title={!writerReady ? "Dedicated admin authorization is not active in this environment." : "Save and audit policy changes"}>{saving ? "Saving…" : "Save policy changes"}</button>
        </section>
      </div>
    </section>}

    {permissions.manageHcRouting && <section className="admin-section" hidden={activeSection !== 'approvals'}>
      <div className="admin-section-heading"><span>02</span><div><h4>Company approval routing</h4><small>Add each company once. HC approvers, CCD requirement, status, editing, and removal are managed from this single list.</small></div></div>

      <section className="admin-routing-table-wrap approval-primary">
        <div className="admin-routing-heading">
          <div><h5>Companies</h5><small>Each row is one complete approval route for a company.</small></div>
          {editableRoutes && <button type="button" onClick={beginAddRoute}>+ Add company</button>}
        </div>
        {routeMessage && <small className="admin-save-success admin-routing-message">{routeMessage}</small>}
        {routeError && !routeDraft && <p className="admin-validation-error" role="alert">{routeError}</p>}
        {companySearch && !visibleRoutes.length && <p className="admin-routing-empty">No companies match your search.</p>}
        {editableRoutes ? <>
          {!editableRoutes.length && <small className="admin-routing-empty">No companies have been added yet.</small>}
          {editableRoutes.length > 0 && <div className="admin-routing-table">
            <div className="admin-routing-row company-routing-row header"><span>Company</span><span>HC approvers</span><span>CCD approval</span><span>Status</span><span>Actions</span></div>
            {visibleRoutes.map((route) => <div className="admin-routing-row company-routing-row" key={`${route.companyKey}-${route.version || "new"}`}>
              <span>{route.companyKey || "Unnamed company"}</span>
              <span data-label="HC approvers">{route.approvers?.map((person, index) => <small className="admin-approver-name" key={person.email || index}>{person.email || person.name || 'Unresolved approver'}</small>)}</span>
              <span data-label="CCD approval" className={`route-state ${route.ccdApprovalRequired === false ? "disabled" : "ready"}`}>{route.ccdApprovalRequired === false ? "Not required" : "Required"}</span>
              <span data-label="Status" className={`route-state ${route.enabled ? "ready" : "disabled"}`}>{!route.enabled ? 'Disabled' : route.approvers?.some(person => !person.resolved) || !route.approvers?.length ? 'Needs attention' : 'Enabled'}</span>
              <div className="admin-row-actions">
                <button type="button" onClick={() => beginEditRoute(route)}>Edit</button>
                <button type="button" className="admin-danger-button" onClick={() => beginRemoveRoute(route)}>Remove</button>
              </div>
            </div>)}
          </div>}

          {routeDraft && !routeDraft.removeMode && <div className="admin-route-editor">
            <h5>{routeDraft.expectedVersion ? `Edit ${routeDraft.companyKey}` : "Add company"}</h5>
            <label>Company<input value={routeDraft.companyKey} disabled={Boolean(routeDraft.expectedVersion)} onChange={(event) => setRouteDraft((current) => ({ ...current, companyKey: event.target.value }))} placeholder="Company name / key" /></label>
            <label className="admin-route-toggle"><input type="checkbox" checked={routeDraft.enabled} onChange={(event) => setRouteDraft((current) => ({ ...current, enabled: event.target.checked }))} /> Enabled</label>
            <AdminUserPicker
              value={routeEmails}
              onChange={(values) => setRouteDraft((current) => ({ ...current, approverEmailsText: values.join('\n') }))}
              getAccessToken={getAccessToken}
              disabled={!writerReady || routeSaving}
              max={10}
              label="HC approvers"
              placeholder="Search HC approver by name or email"
              help="Search Microsoft Entra by name or email. Saving an enabled company automatically synchronizes HC.Approver."
            />
            <small>Enabled companies require at least one HC approver.</small>
            <label className="admin-route-toggle"><input type="checkbox" checked={routeDraft.ccdApprovalRequired} onChange={(event) => setRouteDraft((current) => ({ ...current, ccdApprovalRequired: event.target.checked }))} /> CCD approval required after HC</label>
            <small>{routeDraft.ccdApprovalRequired ? "After HC approval, the request continues to Central CCD Approvers." : "After HC approval, the request goes directly to Microsoft 365 processing."}</small>
            <label className="admin-change-reason">Change reason<textarea rows="2" maxLength="500" value={routeReason} onChange={(event) => setRouteReason(event.target.value)} placeholder="Why is this company approval route changing?" /></label>
            {!routeEditReady && <small className="admin-validation-error">Enter a company and at least one HC approver for an enabled route.</small>}
            {routeReason.length > 0 && !routeReasonReady && <small className="admin-validation-error">Enter 5–500 characters for the audit reason.</small>}
            {routeError && <small className="admin-validation-error">{routeError}</small>}
            <div className="admin-route-actions"><button type="button" onClick={cancelRouteEdit} disabled={routeSaving}>Cancel</button><button type="button" onClick={saveRoute} disabled={!writerReady || !routeEditReady || !routeReasonReady || routeSaving}>{routeSaving ? "Saving…" : "Save company"}</button></div>
          </div>}

          {routeDraft?.removeMode && <div className="admin-route-editor admin-remove-editor">
            <h5>Remove {routeDraft.companyKey}</h5>
            <small>Pending HC requests use the current route. Removing it can change who may approve them or require fallback HC/support. The CCD policy already captured on submitted requests remains unchanged.</small>
            <label className="admin-change-reason">Change reason<textarea rows="2" maxLength="500" value={routeReason} onChange={(event) => setRouteReason(event.target.value)} placeholder="Why is this company being removed from approval routing?" /></label>
            {routeReason.length > 0 && !routeReasonReady && <small className="admin-validation-error">Enter 5–500 characters for the audit reason.</small>}
            {routeError && <small className="admin-validation-error">{routeError}</small>}
            <div className="admin-route-actions"><button type="button" onClick={cancelRouteEdit} disabled={routeSaving}>Cancel</button><button type="button" className="admin-danger-button" onClick={removeRoute} disabled={!writerReady || !routeReasonReady || routeSaving}>{routeSaving ? "Removing…" : "Remove company"}</button></div>
          </div>}
        </> : <>
          {!routes.length && <small className="admin-routing-empty">No company routing rows were returned.</small>}
          {routes.length > 0 && <div className="admin-routing-table">
            <div className="admin-routing-row company-routing-row header"><span>Company</span><span>HC approvers</span><span>CCD approval</span><span>Status</span><span>Actions</span></div>
            {visibleRoutes.map((route) => <div className="admin-routing-row company-routing-row" key={`${route.companyKey}-${route.state}`}>
              <span>{route.companyKey || "Unnamed company"}</span>
              <span>{route.approverCount}</span>
              <span>{route.ccdApprovalRequired === false ? "Not required" : "Required"}</span>
              <span className={`route-state ${route.state}`}>{routeLabel(route.state)}</span>
              <div className="admin-row-actions"><button disabled>Edit</button><button disabled>Remove</button></div>
            </div>)}
          </div>}
        </>}
      </section>

      <div className="admin-settings-grid approval-secondary">
        <section className="admin-settings-card">
          <h5>Central fallback HC</h5>
          <small>Source: {currentSettings.fallbackHc?.source === "admin-settings" ? "Admin settings store" : "Environment default"}</small>
          {fallbackEditable ? <>
            <AdminUserPicker
              value={fallbackEmails}
              onChange={(values) => setFallbackEmailsText(values.join('\n'))}
              getAccessToken={getAccessToken}
              disabled={!writerReady || fallbackSaving}
              max={10}
              label="Fallback HC approvers"
              placeholder="Search fallback HC by name or email"
              help="Search Microsoft Entra by name or email. Remove all selected users to disable the fallback route."
            />
            <small>Used only when no valid company-specific HC route exists.</small>
            <label className="admin-change-reason">Change reason<textarea rows="2" maxLength="500" value={fallbackReason} onChange={(event) => setFallbackReason(event.target.value)} placeholder="Why is the central fallback changing?" /></label>
            {!fallbackDraftReady && <small className="admin-validation-error">Central fallback supports at most 10 approvers.</small>}
            {fallbackReason.length > 0 && !fallbackReasonReady && <small className="admin-validation-error">Enter 5–500 characters for the audit reason.</small>}
            {fallbackError && <small className="admin-validation-error">{fallbackError}</small>}
            {fallbackMessage && <small className="admin-save-success">{fallbackMessage}</small>}
            <button onClick={saveFallback} disabled={!writerReady || !fallbackDraftReady || !fallbackReasonReady || fallbackSaving}>{fallbackSaving ? "Saving…" : "Save fallback HC"}</button>
          </> : <p>{currentSettings.fallbackHc?.configured ? `${currentSettings.fallbackHc.approverCount} fallback approver(s) configured.` : "No central fallback HC is configured."}</p>}
        </section>
      </div>
    </section>}

    {permissions.manageNotifications && <section className="admin-section" hidden={activeSection !== 'notifications'}>
      <div className="admin-section-heading"><span>03</span><div><h4>{permissions.viewSender ? 'Notifications & sender' : 'Notifications'}</h4><small>Notification-only controls. They do not grant approval authority.</small></div></div>
      <div className="admin-settings-grid single-column">
        <section className="admin-settings-card admin-notification-card">
          <h5>Support alerts</h5>
          <small>Source: {currentSettings.notifications?.source === "admin-settings" ? "Admin settings store" : "Environment defaults"}</small>
          {notificationEditable ? <>
            <AdminUserPicker
              value={supportEmails}
              onChange={(values) => setSupportEmailsText(values.join('\n'))}
              getAccessToken={getAccessToken}
              disabled={!writerReady || notificationSaving}
              max={10}
              label="Support alert recipients"
              placeholder="Search support recipient by name or email"
              help="Search Microsoft Entra by name or email, then select the recipients who should receive workflow support alerts."
            />
            <small>Receives missing-HC routing alerts and update-failure support copies.</small>
            <div className="admin-callout"><strong>CCD approval delivery is separate</strong><span>Pending CCD requests and CCD reminders are sent only to the Central CCD Approvers list in Approval Management.</span></div>
            {permissions.viewSender && <><label>Sender mailbox <span className="admin-protected-badge">Exchange RBAC protected</span><input value={currentSettings.notifications?.notificationSender || ""} readOnly /></label>
            <div className="admin-callout admin-callout-warm"><strong>Sender permission is separate</strong><span>Changing the sender address alone never grants send permission. A new mailbox must first be included in the approved Exchange Application RBAC scope and validated.</span></div></>}
            <label className="admin-change-reason">Change reason<textarea rows="2" maxLength="500" value={notificationReason} onChange={(event) => setNotificationReason(event.target.value)} placeholder="Why are support notification recipients changing?" /></label>
            {!notificationDraftReady && <small className="admin-validation-error">Support recipients require 1–10 users.</small>}
            {notificationReason.length > 0 && !notificationReasonReady && <small className="admin-validation-error">Enter 5–500 characters for the audit reason.</small>}
            {notificationError && <small className="admin-validation-error">{notificationError}</small>}
            {notificationMessage && <small className="admin-save-success">{notificationMessage}</small>}
            <button onClick={saveNotifications} disabled={!writerReady || !notificationDraftReady || !notificationReasonReady || notificationSaving}>{notificationSaving ? "Saving…" : "Save support recipients"}</button>
          </> : <>
            <label>Support alert recipients<input value={(currentSettings.notifications?.supportEmails || []).join(", ")} readOnly /></label>
            {permissions.viewSender && <><label>Sender mailbox<input value={currentSettings.notifications?.notificationSender || ""} readOnly /></label>
            <small>The sender mailbox is separately protected by Exchange Application RBAC.</small></>}
          </>}
        </section>
      </div>
    </section>}

    {permissions.viewAccess && <section className="admin-section" hidden={activeSection !== 'access'}>
      <div className="admin-section-heading"><span>04</span><div><h4>Access & roles</h4><small>Review effective application access and its authoritative management source.</small></div></div>
      <p>HC is managed from Companies &amp; approvals, Central CCD from Central CCD Approvers, and web-managed application roles from Access &amp; roles. SuperAdmin remains Microsoft Entra-only.</p>
    </section>}

    {permissions.viewSecurity && <section className="admin-section" hidden={activeSection !== 'security'}>
      <div className="admin-section-heading"><span>05</span><div><h4>Security status</h4><small>Read-only controls that explain the live protection boundaries.</small></div></div>
      <div className="admin-settings-grid single-column">
        <section className="admin-settings-card">
          <div className="admin-security-row"><span>Outlook actions</span><strong>{currentSettings.security?.outlookActionsEnabled ? "Enabled" : "Disabled"}</strong></div>
          <div className="admin-security-row"><span>Pilot restriction</span><strong>{currentSettings.security?.pilotRestrictionEnabled ? "Enabled" : "Disabled"}</strong></div>
          <div className="admin-security-row"><span>Dedicated admin settings writer</span><strong>{currentSettings.security?.dedicatedAdminWriterConfigured ? "Configured" : "Locked"}</strong></div>
          <div className="admin-security-row"><span>Admin setting writes</span><strong>{writerReady ? "Enabled" : "Locked"}</strong></div>
          <div className="admin-security-row"><span>Role-management writer</span><strong>Not provisioned</strong></div>
          <div className="admin-security-row"><span>Runtime IDs</span><strong>Read only</strong></div>
          <div className="admin-security-row"><span>Worker / Gate controls</span><strong>Not editable here</strong></div>
        </section>
      </div>
    </section>}

    </fieldset>
    <div hidden={activeSection !== 'approvals'}>{ccdPanel}</div>
    {permissions.viewAccess && <div hidden={activeSection !== 'access'}>{accessPanel}</div>}
    <div className="admin-settings-footer">
      <strong>{writerReady ? "Changes are recorded with your account and change reason." : "Viewing settings. Editing is currently locked."}</strong>
    </div>
  </section>;
}
